



















































































































































































































































































import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator'
import { ActiveCampaignCredentials, MailchimpCredentials } from '@/api/model/credentials'
import { CredentialsType, ReferralStatus, ReferralType, SmartMarketingSelection } from '@/api/types'
import { Branch } from '@/api/model/branch'
import { ActiveCampaignAPI } from '@/api/client/active-campaign'
import { RealworksAPI } from '@/api/client/realworks'
import { RealworksKenmerkAPI, WebhookAPI } from '@/api/backend-client'
import { SmartMarketingAPI } from '@/api/client/smart-marketing'
import { gsap } from 'gsap'
import { Webhook } from '@/api/model/webhook'

@Component
export default class EditSmartMarketingForm extends Vue {
    @Inject() readonly reloadCompany: () => Promise<void>
    @Prop({ required: true }) currentCredentials!: ActiveCampaignCredentials|MailchimpCredentials
    @Prop({ required: true }) credentials!: ActiveCampaignCredentials|MailchimpCredentials
    loadingContactsCount: boolean = false
    contactsCount: number = 0
    activating: boolean = false
    validating: boolean = false
    valid: boolean = null
    eventTrackingEnabled: boolean = false
    loadingRealworksKenmerken: boolean = true
    realworksKenmerken = []
    realworksAgendaEnabled: boolean = false
    loadingRealworksAgendatypes: boolean = true
    realworksAgendatypes = []
    realworksTaakEnabled: boolean = false
    loadingRealworksTaaktypes: boolean = true
    realworksTaaktypes = []
    progressStep: number = 0
    webhooks: Webhook[] = []
    loadingWebhooks: boolean = true

    mounted() {
        this.loadContent()
    }

    async loadContent(): Promise<void> {
        this.setEventTracking()
        await this.loadRealworksKenmerken()
        this.setRealworksAgenda()
        await this.loadRealworksAgendatypes()
        this.setRealworksTaak()
        await this.loadRealworksTaaktypes()

        if (this.isActiveCampaign) {
            await this.loadWebhooks()
        }
    }

    async loadWebhooks(): Promise<void> {
        this.loadingWebhooks = true
        this.webhooks = await WebhookAPI.getAll()
        this.webhooks.sort((a, b) => a.name.localeCompare(b.name))
        this.loadingWebhooks = false
    }

    async copyWebhook(webhook: Webhook): Promise<void> {
        this.copyToClipboard(webhook.url)
        this.$notify({
            group: 'validation',
            text: this.$t('forms.edit_smart_marketing_form.copiedWebhookUrl').toString(),
        })
    }

    async createWebhook(): Promise<void> {
        await this.$router.push({
            name: 'company_settings_webhook_create',
        })
    }

    async editWebhook(webhook: Webhook): Promise<void> {
        await this.$router.push({
            name: 'company_settings_webhook_edit',
            params: {
                id: webhook.id,
            },
        })
    }

    async deleteWebhook(webhook: Webhook): Promise<void> {
        if (confirm(`Weet je zeker dat je de webhook genaamd '${webhook.name}' wil verwijderen?`)) {
            this.loadingWebhooks = true
            await WebhookAPI.delete(webhook.id)
            await this.loadWebhooks()
        }
    }

    @Watch('credentials.includeInternalContacts')
    @Watch('credentials.includeExternalContacts')
    @Watch('credentials.includeRealworksRelaties')
    @Watch('credentials.syncedRealworksKenmerken')
    @Watch('credentials.updatedFrom')
    @Watch('credentials.updatedUntil')
    async reloadContactsCount() {
        if (this.loadingContactsCount) {
            return
        }

        this.loadingContactsCount = true
        this.contactsCount = await SmartMarketingAPI.getContactsCount(this.credentials as SmartMarketingSelection)
        this.loadingContactsCount = false
    }

    animateShow(element: HTMLDivElement, onComplete: () => void) {
        gsap.set(element, {
            opacity: 0,
        })

        gsap.to(element, {
            duration: .2,
            opacity: 1,
            onComplete,
        })
    }

    animateHide(element: HTMLDivElement, onComplete: () => void) {
        gsap.to(element, {
            duration: .2,
            opacity: 0,
            onComplete,
        })
    }

    async progressIncrement(delay: number = 1000): Promise<void> {
        return new Promise(resolve => setTimeout(() => {
            this.progressStep++
            resolve()
        }, delay + Math.random() * (3000 - 1000) + 1000))
    }

    get progressMessages(): string[] {
        return [
            'Bezig met activeren van Smart Marketing..',
            'Verbinding maken met externe server..',
            'Beveiligde verbinding is opgezet..',
            'Bezig met AVG kaders opzetten..',
            'Lijsten aanmaken..',
            'Velden aanmaken..',
            'Terugkoppeling is ingesteld..',
            'Bezig met data overzetten..',
            'Externe terugkoppeling instellen..',
            'Nog even geduld..',
            'SmartMarketing is ingeschakeld!',
        ]
    }

    async activate() {
        this.activating = true
        this.progressStep = 0

        ActiveCampaignAPI
            .activate(this.credentials as ActiveCampaignCredentials)
            .then(() => {
                this.progressStep = 9
                setTimeout(() => {
                    this.reloadCompany().then()
                    this.activating = false
                }, 2000)
            })

        // These will sequentially progress the message on screen.
        await this.progressIncrement()
        await this.progressIncrement()
        await this.progressIncrement()
        await this.progressIncrement()
        await this.progressIncrement()
        await this.progressIncrement()
        await this.progressIncrement()
        await this.progressIncrement()
        await this.progressIncrement()
    }

    async validate() {
        this.valid = null

        this.validating = true
        this.valid = await ActiveCampaignAPI.validateCredentials(this.credentials as ActiveCampaignCredentials)
        this.validating = false
    }

    setEventTracking() {
        if (this.credentials instanceof MailchimpCredentials) {
            this.eventTrackingEnabled = false
        }

        if (this.credentials instanceof ActiveCampaignCredentials) {
            if (this.credentials.eventTrackingAccountId || this.credentials.eventTrackingKey) {
                this.eventTrackingEnabled = true
            } else {
                this.eventTrackingEnabled = false
            }
        }
    }

    resetEventTracking() {
        if (this.credentials instanceof ActiveCampaignCredentials) {
            this.credentials.eventTrackingAccountId = null
            this.credentials.eventTrackingKey = null
        }
    }

    @Watch('eventTrackingEnabled')
    onEventTrackingEnabledChanged(eventTrackingEnabled) {
        if (!eventTrackingEnabled) {
            this.resetEventTracking()
        }
    }

    get hasRealworks() {
        return this.$currentCompany.hasCredentialsWithType(CredentialsType.REALWORKS)
    }

    setRealworksAgenda() {
        if (this.credentials instanceof MailchimpCredentials) {
            this.realworksAgendaEnabled = false
        }

        if (this.credentials instanceof ActiveCampaignCredentials) {
            if (this.credentials.syncedRealworksAgendatypes.length > 0) {
                this.realworksAgendaEnabled = true
            } else {
                this.realworksAgendaEnabled = false
            }
        }
    }

    resetRealworksAgenda() {
        if (this.credentials instanceof ActiveCampaignCredentials) {
            this.credentials.syncedRealworksAgendatypes = []
        }
    }

    @Watch('realworksAgendaEnabled')
    onRealworksAgendaEnabledChanged(realworksAgendaEnabled) {
        if (!realworksAgendaEnabled) {
            this.resetRealworksAgenda()
        }
    }

    setRealworksTaak() {
        if (this.credentials instanceof MailchimpCredentials) {
            this.realworksTaakEnabled = false
        }

        if (this.credentials instanceof ActiveCampaignCredentials) {
            if (this.credentials.syncedRealworksTaaktypes.length > 0) {
                this.realworksTaakEnabled = true
            } else {
                this.realworksTaakEnabled = false
            }
        }
    }

    resetRealworksTaak() {
        if (this.credentials instanceof ActiveCampaignCredentials) {
            this.credentials.syncedRealworksTaaktypes = []
        }
    }

    @Watch('realworksTaakEnabled')
    onRealworksTaakEnabledChanged(realworksTaakEnabled) {
        if (!realworksTaakEnabled) {
            this.resetRealworksTaak()
        }
    }

    get yesNoOptions() {
        return [
            { label: this.$t('yes'), value: true },
            { label: this.$t('no'), value: false },
        ]
    }

    get platform() {
        return this.credentials.credentialsType
    }

    set platform(platform) {
        if (this.platform === platform) {
            return
        }

        this.$emit('change-platform', platform)
    }

    get platformOptions() {
        return [
            { label: 'Mailchimp', value: CredentialsType.MAILCHIMP },
            { label: 'ActiveCampaign', value: CredentialsType.ACTIVE_CAMPAIGN },
        ]
    }

    getWebhookUrl(referralType: ReferralType) {
        return process.env.VUE_APP_KEYVUE_BACKEND_BASEURL +
            '/api/v1/service/active_campaign/referral' +
            '?company=' + this.$currentCompany.id +
            '&type=' + referralType
    }

    copyWebhookUrl(referralType: ReferralType) {
        this.copyToClipboard(this.getWebhookUrl(referralType))
        this.$notify({
            group: 'validation',
            text: this.$t('forms.edit_smart_marketing_form.copiedWebhookUrl').toString(),
        })
    }

    getEnrichWebhookUrl() {
        return process.env.VUE_APP_KEYVUE_BACKEND_BASEURL +
            '/api/v1/service/active_campaign/promo/enrich' +
            '?company=' + this.$currentCompany.id
    }

    copyEnrichWebhookUrl() {
        this.copyToClipboard(this.getEnrichWebhookUrl())
        this.$notify({
            group: 'validation',
            text: this.$t('forms.edit_smart_marketing_form.copiedWebhookUrl').toString(),
        })
    }

    // Source: https://hackernoon.com/copying-text-to-clipboard-with-javascript-df4d4988697f
    copyToClipboard(str: string) {
        const el = document.createElement('textarea')
        el.value = str
        el.setAttribute('readonly', '')
        el.style.position = 'absolute'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        const selected = document.getSelection().rangeCount > 0 ? document.getSelection().getRangeAt(0) : false
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        if (selected) {
            document.getSelection().removeAllRanges()
            document.getSelection().addRange(selected)
        }
    }

    get internalReferralTypes() {
        return this.$currentCompany.handledInternalReferralTypes
            .map((type: string) => ({
                label: this.$t(`referral.type.${type}`) as string,
                value: type,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    get availableBranches() {
        return this.$currentCompany.branches
            .map((element: Branch) => ({
                label: element.name,
                value: element.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    get availableStatuses() {
        const statuses = [
            ReferralStatus.PROCESSED,
            ReferralStatus.NEW,
        ]

        return statuses.map(status => {
            return {
                label: this.$t(`referral.status.${status}`),
                value: status,
            }
        })
    }

    async loadRealworksKenmerken() {
        this.loadingRealworksKenmerken = true

        const realworksKenmerken = await RealworksKenmerkAPI.getAll()
        this.realworksKenmerken = realworksKenmerken
                .map(kenmerk => ({
                    label: kenmerk.omschrijving,
                    value: kenmerk.id,
                }))
                .sort((a, b) => a.label.localeCompare(b.label))

        this.loadingRealworksKenmerken = false
    }

    async loadRealworksAgendatypes() {
        this.loadingRealworksAgendatypes = true

        const realworksAgendatypes = await RealworksAPI.getAgendatypes()
        this.realworksAgendatypes = realworksAgendatypes
                .map(agendatype => ({
                    label: agendatype,
                    value: agendatype,
                }))
                .sort((a, b) => a.label.localeCompare(b.label))

        this.loadingRealworksAgendatypes = false
    }

    async loadRealworksTaaktypes() {
        this.loadingRealworksTaaktypes = true

        const realworksTaaktypes = await RealworksAPI.getTaaktypes()
        this.realworksTaaktypes = realworksTaaktypes
                .map(taaktype => ({
                    label: taaktype,
                    value: taaktype,
                }))
                .sort((a, b) => a.label.localeCompare(b.label))

        this.loadingRealworksTaaktypes = false
    }

    get isActiveCampaign(): boolean {
        return this.credentials.credentialsType === CredentialsType.ACTIVE_CAMPAIGN
    }

    get isActivated(): boolean {
        return this.currentCredentials.listId !== null
    }

    get canBeActivated(): boolean {
        if (this.isActivated) {
            return false
        }

        if (this.currentCredentials instanceof MailchimpCredentials && this.credentials instanceof MailchimpCredentials) {
            return this.currentCredentials.server !== null
                && this.currentCredentials.server === this.credentials.server
                && this.currentCredentials.apiKey !== null
                && this.currentCredentials.apiKey === this.credentials.apiKey
        }

        if (this.currentCredentials instanceof ActiveCampaignCredentials && this.credentials instanceof ActiveCampaignCredentials) {
            return this.currentCredentials.apiUrl !== null
                && this.currentCredentials.apiUrl === this.credentials.apiUrl
                && this.currentCredentials.apiKey !== null
                && this.currentCredentials.apiKey === this.credentials.apiKey
        }

        return false
    }
}
